import * as React from 'react';
import Box from '@mui/material/Box';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { visuallyHidden } from '@mui/utils';
import { IImageThumbnail } from '../../../Entities';
import { preloadImage } from '../../../cache';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';

type ImageSelectStitchSideButtonsProps = {
    image: IImageThumbnail;
};

export function ImageSelectStitchSideButtons({ image }: ImageSelectStitchSideButtonsProps) {
    let apiUrl = useApiUrl();

    function preloadHighResImage(e: React.MouseEvent) {
        e.stopPropagation();
        if (image.highResolutionImageReference) {
            preloadImage(apiUrl + '/file/' + image.highResolutionImageReference);
        }
    }

    function stopPropagation(event: React.MouseEvent) {
        event.stopPropagation();
    }

    function addToggleBehavior(event: React.MouseEvent, inputId: string) {
        event.stopPropagation();
        let input = document.getElementById(inputId) as HTMLInputElement;

        if (input.checked) {
            event.preventDefault();
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    return (
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
                display: 'flex',
                gap: '8px'
            }}
        >
            <Box
                component="span"
                role="radiogroup"
                aria-label="Left image"
                sx={{
                    display: 'inline-flex',
                    '& input:checked + label': {
                        backgroundColor: '#3D9970',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#2d7a56'
                        }
                    }
                }}
            >
                <Box
                    component="input"
                    type="radio"
                    name="leftImageId"
                    value={image.id}
                    id={`stitch-left-${image.id}`}
                    aria-label={'Select ' + image.filename + ' as left image'}
                    sx={visuallyHidden}
                    onClick={stopPropagation}
                />
                <Box
                    component="label"
                    htmlFor={`stitch-left-${image.id}`}
                    onMouseEnter={preloadHighResImage}
                    onClick={(event: React.MouseEvent) => {
                        addToggleBehavior(event, `stitch-left-${image.id}`);
                    }}
                    sx={{
                        minWidth: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                        px: 1,
                        py: 0.5,
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        lineHeight: 1.75,
                        '&:hover': {
                            backgroundColor: '#e0e0e0'
                        }
                    }}
                >
                    <ImageIcon fontSize="small" />
                    <ArrowBackIcon fontSize="small" />
                </Box>
            </Box>
            <Box
                component="span"
                role="radiogroup"
                aria-label="Right image"
                sx={{
                    display: 'inline-flex',
                    '& input:checked + label': {
                        backgroundColor: '#3D9970',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#2d7a56'
                        }
                    }
                }}
            >
                <Box
                    component="input"
                    type="radio"
                    name="rightImageId"
                    value={image.id}
                    id={`stitch-right-${image.id}`}
                    aria-label={'Select ' + image.filename + ' as right image'}
                    sx={visuallyHidden}
                    onClick={stopPropagation}
                />
                <Box
                    component="label"
                    htmlFor={`stitch-right-${image.id}`}
                    onMouseEnter={preloadHighResImage}
                    onClick={(event: React.MouseEvent) => {
                        addToggleBehavior(event, `stitch-right-${image.id}`);
                    }}
                    sx={{
                        minWidth: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                        px: 1,
                        py: 0.5,
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        lineHeight: 1.75,
                        '&:hover': {
                            backgroundColor: '#e0e0e0'
                        }
                    }}
                >
                    <ArrowForwardIcon fontSize="small" />
                    <ImageIcon fontSize="small" />
                </Box>
            </Box>
        </Box>
    );
}
